/*
 * Per-user enrollment status.
 *
 * Status tells an admin whether an enrollment is real rather than a stale row —
 * hence the device name and a relative last-used, not just a badge.
 *
 * Delivery lives in RegisterDeviceDialog, opened from here or from the row's ⋯
 * menu. It is gated on ADMIN / SUPERADMIN: the dialog mints an invite that
 * registers a device as this user, and a control that exists and then 403s is a
 * worse answer than one that was never offered.
 */
import { useCallback, useEffect, useState } from "react";
import { Smartphone, UserPlus } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../constants";
import { logOutUser } from "../../common";
import { Modal, Switch, Tooltip } from "antd";
import {
  dispatchAdminDevicesList,
  dispatchAdminDevicesPush,
  dispatchAdminDevicesRevoke,
  dispatchGetEnrollmentStatus,
  dispatchGetEnrollmentSummary,
} from "../../utils/apiservice";
import { canInviteOthers, providerName } from "./enrollmentCommon";
import RegisterDeviceDialog from "./RegisterDeviceDialog";

const relUsed = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const STATUS_TONE = {
  active:  { fg: AN_COLORS.success, bg: "var(--an-successBg)", bd: "var(--an-successBd)", label: "Enrolled" },
  pending: { fg: AN_COLORS.warn,    bg: "#FFF8EC", bd: "#F3E2C0", label: "Invite pending" },
  none:    { fg: AN_COLORS.muted,   bg: AN_COLORS.bg, bd: AN_COLORS.line, label: "Not enrolled" },
};

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.none;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px",
      borderRadius: 20, background: tone.bg, border: `1px solid ${tone.bd}`,
      fontSize: 11.5, fontWeight: 500, color: tone.fg, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: tone.fg }} />
      {tone.label}
    </span>
  );
}

/*
 * Every phone on the account (a user may have several, up to the org's limit),
 * with a way to sign one out — a lost phone, a phone that was never theirs.
 * Signed-out phones stay listed, greyed, so an admin can see what happened.
 */
function UserDevices({ email, refreshKey, onNotify, onChanged }) {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!email) { setDevices([]); return; }
    dispatchAdminDevicesList({
      payload: { email },
      onSuccess: (res) => { setDevices(res?.devices || []); setError(""); },
      onFailure: ({ message: m, isLogout }) => {
        if (isLogout) { logOutUser(); return; }
        setError(m || "Could not load this user's phones.");
      },
      onNotFound: () => setDevices([]),
      onForbidden: () => setDevices([]),
      onError: ({ message: m }) => setError(m || "Could not load this user's phones."),
    });
  }, [email]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const revoke = (d) => {
    Modal.confirm({
      title: `Sign out ${d.deviceName || "this phone"}?`,
      content: "It stops receiving approval requests straight away and is told it was signed out. The user can add it back with a new invite.",
      okText: "Sign out",
      okButtonProps: { danger: true },
      onOk: () => new Promise((resolve) => {
        setBusy(d.deviceId);
        dispatchAdminDevicesRevoke({
          payload: { deviceId: d.deviceId, reason: "signed out by an administrator" },
          onSuccess: () => {
            setBusy(null);
            onNotify?.({ type: "success", content: `${d.deviceName || "The phone"} was signed out.` });
            load();
            onChanged?.();
            resolve();
          },
          onFailure: ({ message: m }) => { setBusy(null); onNotify?.({ type: "error", content: m || "Could not sign the phone out." }); resolve(); },
          onNotFound: () => { setBusy(null); load(); resolve(); },
          onError: ({ message: m }) => { setBusy(null); onNotify?.({ type: "error", content: m || "Could not sign the phone out." }); resolve(); },
        });
      }),
    });
  };

  // Stops sign-in requests to one phone without signing it out — a phone in a
  // drawer, or one the user wants only as a backup.
  const setPush = (d, enabled) => {
    setBusy(d.deviceId);
    setDevices((list) => list.map((x) => (x.deviceId === d.deviceId ? { ...x, pushEnabled: enabled } : x)));
    dispatchAdminDevicesPush({
      payload: { deviceId: d.deviceId, enabled },
      onSuccess: () => setBusy(null),
      onFailure: ({ message: m }) => { setBusy(null); load(); onNotify?.({ type: "error", content: m || "Could not change this phone." }); },
      onNotFound: () => { setBusy(null); load(); },
      onError: ({ message: m }) => { setBusy(null); load(); onNotify?.({ type: "error", content: m || "Could not change this phone." }); },
    });
  };

  if (error) return <div style={{ marginTop: 10, fontSize: 12.5, color: AN_COLORS.danger }}>{error}</div>;
  if (!devices || devices.length === 0) return null;

  return (
    <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
      {devices.map((d) => {
        const used = d.lastUsedAt ? relUsed(d.lastUsedAt) : null;
        const os = d.osVersion ? `${d.platform === "ios" ? "iOS" : "Android"} ${d.osVersion}` : d.platform;
        return (
          <div key={d.deviceId} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
            border: `1px solid ${AN_COLORS.line}`, borderRadius: 8, background: AN_COLORS.surface,
            opacity: d.active === false ? 0.55 : 1, fontSize: 12.5, color: AN_COLORS.ink2,
          }}>
            <Smartphone size={14} color={AN_COLORS.muted} />
            <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{d.deviceName || "Unnamed device"}</span>
            {os && <span style={{ color: AN_COLORS.muted }}>{os}</span>}
            {d.appVersion && <span style={{ color: AN_COLORS.muted, fontFamily: AN_MONO, fontSize: 11 }}>app {d.appVersion}</span>}
            <span style={{ color: AN_COLORS.muted }}>
              {d.active === false ? "· signed out" : used ? `· last used ${used}` : "· not used yet"}
            </span>
            {d.active !== false && d.pushEnabled === false && (
              <span style={{ color: AN_COLORS.warn }}>· notifications off</span>
            )}
            <span style={{ flex: 1 }} />
            {d.active !== false && (
              <Tooltip title="Send sign-in requests to this phone">
                <Switch
                  size="small"
                  checked={d.pushEnabled !== false}
                  loading={busy === d.deviceId}
                  disabled={busy !== null && busy !== d.deviceId}
                  onChange={(on) => setPush(d, on)}
                  aria-label={`Send sign-in requests to ${d.deviceName || "this phone"}`}
                />
              </Tooltip>
            )}
            {d.active !== false && (
              <button type="button" disabled={busy === d.deviceId} onClick={() => revoke(d)} style={{
                border: "none", background: "transparent", padding: 0, cursor: "pointer",
                color: AN_COLORS.danger, fontSize: 12.5, fontWeight: 500, fontFamily: AN_FONT,
              }}>{busy === d.deviceId ? "Signing out…" : "Sign out"}</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

const LABEL = {
  fontFamily: AN_MONO, fontSize: 10, letterSpacing: 1.2,
  textTransform: "uppercase", color: AN_COLORS.muted,
};

/**
 * @param userId   AD user id (or console user id) for the status/email calls
 * @param email    the user's email. adminInvite is keyed on this alone.
 * @param domain   the user's directory (FQDN or the label the admin typed). When
 *                 given, `enrollmentApplies` for that directory is resolved
 *                 first and the panel is replaced by "MFA managed by X" when it
 *                 is false — the same guard the directory-level panel applies.
 *                 Without it the controls show, which is right for console
 *                 users: they are not AD logons.
 * @param onNotify optional ({ type, content }) toast sink from the host screen
 * @param openSignal changing this to a truthy value opens the dialog — lets the
 *                 row's ⋯ menu drive the same dialog this panel owns.
 */
export default function UserEnrollment({ userId, email, domain, onNotify, openSignal, userType = "directory" }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // undefined = still resolving · null = not applicable or unresolvable ·
  // true/false = the backend's verdict. The undefined state matters: the
  // actions must not flash into view on a vendor-managed org and then vanish.
  const [applies, setApplies] = useState(undefined);
  const [vendorProvider, setVendorProvider] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [devicesKey, setDevicesKey] = useState(0);

  const isAdmin = canInviteOthers();

  const loadStatus = useCallback(() => {
    setLoading(true);
    setError("");
    dispatchGetEnrollmentStatus({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), userId, email },
      onSuccess: (res) => { setLoading(false); setStatus(res); },
      onNotFound: () => {
        setLoading(false);
        // No enrollment row is a legitimate answer, not an error state.
        setStatus({ enrollmentStatus: "none" });
      },
      onFailure: ({ message: m, isLogout }) => {
        setLoading(false);
        if (isLogout) { logOutUser(); return; }
        setError(m || "Could not load enrollment status.");
      },
      onError: ({ message: m }) => {
        setLoading(false);
        setError(m || "Could not load enrollment status.");
      },
    });
  }, [userId, email]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Let the row's ⋯ menu open this panel's dialog, so both entry points share
  // one invite lifecycle instead of each minting its own.
  useEffect(() => { if (openSignal) setDialogOpen(true); }, [openSignal]);

  // Resolve whether enrollment applies to this user's directory at all. For
  // okta / duo / azure_ad the second factor lives in the vendor, so registering
  // a device here would produce one that is never challenged. The flag comes
  // from the backend and is never re-derived from the provider name.
  useEffect(() => {
    // No directory to check against — nothing to gate on. Resolve immediately
    // so the actions are not held behind a check that will never run.
    if (!domain || domain === "—") { setApplies(null); return; }
    setApplies(undefined);
    let live = true;
    dispatchGetEnrollmentSummary({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), domain, days: 7 },
      onSuccess: (res) => {
        if (!live) return;
        setApplies(res?.enrollmentApplies !== false);
        setVendorProvider(res?.provider);
      },
      // An unresolvable domain is not a reason to hide a user's own enrollment
      // state; fall back to showing it.
      onNotFound: () => { if (live) setApplies(null); },
      onFailure: () => { if (live) setApplies(null); },
      onError: () => { if (live) setApplies(null); },
    });
    return () => { live = false; };
  }, [domain]);

  const st = status?.enrollmentStatus || "none";
  const lastUsed = status?.lastUsedAt ? relUsed(status.lastUsedAt) : null;
  // A directory was given but its verdict hasn't arrived. Hold the actions
  // rather than showing them and taking them away.
  const guardPending = !!domain && applies === undefined;

  // Vendor-managed: no status, no invite, no QR. Registering a device with the
  // AuthNull app would never receive a challenge.
  if (applies === false) {
    return (
      <div style={{ fontFamily: AN_FONT, fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.5 }}>
        <span style={{ ...LABEL, marginRight: 10 }}>Authenticator</span>
        MFA managed by {providerName(vendorProvider)} — this user's second factor is enrolled there,
        not with AuthNull.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: AN_FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <span style={LABEL}>Authenticator</span>

        {loading ? (
          <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>Checking…</span>
        ) : error ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12.5, color: AN_COLORS.danger }}>{error}</span>
            <button type="button" onClick={loadStatus} style={{
              border: "none", background: "transparent", padding: 0, cursor: "pointer",
              color: AN_COLORS.primary, fontSize: 12.5, fontWeight: 500, fontFamily: AN_FONT,
            }}>Retry</button>
          </span>
        ) : (
          <>
            <StatusPill status={st} />

            {/* A device name and a real last-used is what separates a live
                enrollment from a stale row. */}
            {st === "active" && (status?.deviceName || lastUsed) && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 12.5, color: AN_COLORS.ink2,
              }}>
                <Smartphone size={13} color={AN_COLORS.muted} />
                {status?.deviceName || "Unnamed device"}
                {status?.platform && <span style={{ color: AN_COLORS.muted }}>({status.platform})</span>}
                {lastUsed && <span style={{ color: AN_COLORS.muted }}>· last used {lastUsed}</span>}
              </span>
            )}

            {st === "pending" && (
              <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>
                Invite sent — waiting for the device to register.
              </span>
            )}

            {st === "none" && !email && (
              // An AD user with an empty `mail` attribute cannot be emailed; the
              // link or QR is the only route.
              <span style={{ fontSize: 12.5, color: AN_COLORS.warn }}>
                No email address on this record — share the invite link or QR instead.
              </span>
            )}
          </>
        )}
      </div>

      {/* Each phone, with sign-out. Admin-only on the server, so only asked
          for when this viewer is one. */}
      {!loading && !error && isAdmin && (
        <UserDevices email={email} refreshKey={devicesKey} onNotify={onNotify} onChanged={loadStatus} />
      )}

      {/* An account may hold several phones, so an enrolled user can still be
          sent an invite for another one. */}
      {!loading && !error && !guardPending && isAdmin && (
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={() => setDialogOpen(true)} style={{
            display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px",
            fontFamily: AN_FONT, fontSize: 12.5, fontWeight: 500, cursor: "pointer",
            color: "#fff", background: AN_COLORS.primary, border: "none", borderRadius: 8,
          }}>
            <UserPlus size={13} />{" "}
            {st === "active" ? "Add another phone" : st === "pending" ? "Re-issue invite" : "Register device"}
          </button>
        </div>
      )}

      {/* Mounted only while open, so the invite-invalidating call cannot fire on
          this panel's render. */}
      {dialogOpen && (
        <RegisterDeviceDialog
          open={dialogOpen}
          email={email}
          userId={userId}
          userType={userType}
          onNotify={onNotify}
          onClose={() => { setDialogOpen(false); loadStatus(); setDevicesKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
